import { useEffect, useState } from "react";
import useSkipper from "@/react/components/table/useSkipper";
import {
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import useTableMutations, {
  KEY,
} from "@/react/components/table/useTableMutations";
import { defaultHiddenColumnDefReducer } from "@/react/components/table/components/ColumnSelector";
import { useTableFilters } from "@/react/components/table/useTableFilters";
import { useTableState } from "@/react/components/table/useTableState";
import { columnWidthMapping } from "@/react/components/table/columns";
import { omit } from "lodash";

/**
 * @typedef {import("@tanstack/table-core").ColumnDef} ColumnDef
 * @typedef {import("@tanstack/table-core").TableOptions} TableOptions
 * @typedef {import("@tanstack/table-core").Table} Table
 * @typedef {import("@tanstack/table-core").Row} Row
 * @typedef {import("@tanstack/table-core").Column} Column
 * @typedef {import("@tanstack/table-core").ColumnFilter} ColumnFilter
 * @typedef {import("@/react/components/table/useTableMutations").TableMutations} TableMutations
 * @typedef {import("@tanstack/table-core").TableState} TableState
 */

/**
 * Wrapper over TanStack v8 `useReactTable` hook
 *
 * @param {Array<*>} initialData
 * @param {Array<import("@tanstack/table-core").ColumnDef>} columns
 * @param {ObjectSchema} schema
 * @param {string} idKey
 * @param {TableOptions} tableOptions Pass any other options to the table, https://tanstack.com/table/v8/docs/api/core/table
 * @param {TableState} stateOptions Pass any other state options to the table state
 * @returns {{table: Table, mutations: TableMutations, filters: {}, state: TableState}}
 */
// TODO: This seems to get called whenever columns are resized in the table, which leads to slow performance if there are many rows.
export const useTable = (
  initialData,
  columns,
  schema,
  idKey,
  tableOptions = null,
  stateOptions = null
) => {
  const [columnDef, setColumnDef] = useState(columns);
  useEffect(() => {
    setColumnDef(columns);
  }, [columns]);

  const mutations = useTableMutations(initialData, idKey, tableOptions);

  const { data, updateRows, queryFunctions } = mutations;

  const state = useTableState();
  const {
    columnVisibility,
    setColumnVisibility,
    rowSelection,
    setRowSelection,
    columnFilters,
    setColumnFilters,
  } = state;

  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();

  const pageSizes = tableOptions?.pageSizes || defaultPageSizes;

  const table = useReactTable({
    data,
    columns: columnDef,
    getRowId: (row) => row[KEY],
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    onColumnFiltersChange: setColumnFilters,
    autoResetPageIndex,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    meta: {
      schema,
      updateRows,
      skipAutoResetPageIndex,
      queryFunctions,
    },
    initialState: {
      pagination: {
        pageSize: pageSizes[0],
        pageSizes,
      },
    },
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
      ...(stateOptions ?? {}),
    },
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
    ...(tableOptions ?? {}),
  });

  useEffect(() => {
    setColumnVisibility(
      table.getAllColumns().reduce(defaultHiddenColumnDefReducer, {})
    );
  }, []);

  // Size the columns appropriately based on the data, not simply the headers
  useEffect(() => {
    table.setColumnSizing(columnWidthMapping(columnDef, data));
  }, [data]);

  // Do the column resizing whenever the pagination changes,
  // so that we size the columns based on the rows actually shown
  // This is in a different useEffect than the other column sizing, because it's an optional feature
  useEffect(() => {
    if (tableOptions?.dynamicColumnResizing) {
      table.setColumnSizing(
        columnWidthMapping(
          columnDef,
          table.getPaginationRowModel().rows.map((row) => row.original)
        )
      );
    }
  }, [table.getState().pagination]);

  const filters = useTableFilters(
    table.getAllColumns(),
    setColumnFilters,
    setColumnDef
  );

  return {
    table,
    filters,
    state,
    mutations,
  };
};

const defaultPageSizes = [25, 50, 100];
